import React, { useEffect } from "react"
import { useInView } from "react-intersection-observer"
import type { ProductComparison } from "components/Shop/PDPPage/PDPPage.constants"
import type { VariantType } from "@shared/graphql.types"
import { CloseIcon } from "components/SVG/CloseIcon"
import { useSegment } from "tracking"
import { OverlayDrawer } from "packages/ui-shared/components/OverlayDrawer"
import { DupeTable } from "./DupeTable"
import { DrawerFooter } from "./DrawerFooter"
import {
  drawerBodyText,
  drawerCHeader,
  titleSection,
  dialog,
  dupesScrollContent,
  dupesScrollBar,
} from "./Drawer.css"

type Props = {
  isOpen: boolean
  onClose: () => void
  productComparisonVariation: string
  variantData: [
    id: VariantType["id"],
    sku: VariantType["sku"],
    pricing: VariantType["pricing"]
  ]
  productComparison: ProductComparison
}

export const ComparisonDrawer: React.FC<Props> = ({
  isOpen,
  onClose,
  productComparison,
  variantData,
  productComparisonVariation,
}) => {
  const { track } = useSegment()
  const isPCV2 = productComparisonVariation === "2"

  const { ref, inView } = useInView({
    threshold: 1,
    triggerOnce: true,
  })

  useEffect(() => {
    if (inView) {
      track({
        event: "Page Element Viewed",
        properties: {
          module_content: "dupes_v3_drawer",
          location_type: "dupes_v3_drawer",
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView])

  return (
    <OverlayDrawer
      isOpen={isOpen}
      anchorPosition="bottom"
      className={`${dialog} ${dupesScrollContent}`}
      handleDismiss={onClose}
    >
      <div ref={ref} className={titleSection}>
        <h3 className={drawerCHeader}>How We Compare</h3>
        <div
          role="button"
          tabIndex={0}
          onClick={onClose}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              onClose()
            }
          }}
        >
          <CloseIcon />
        </div>
      </div>
      <div className={dupesScrollBar}>
        <p className={drawerBodyText}>
          {isPCV2 ? (
            <>
              Comparison is based on a luxury product of similar:
              <ul>
                {["Quality", "Efficacy", "Size/Weight"].map((bulletPoint) => (
                  <li
                    key={`bullet-point-${bulletPoint}`}
                    style={{
                      listStyleType: "square",
                      marginLeft: "1rem",
                    }}
                  >
                    {bulletPoint}
                  </li>
                ))}
              </ul>
              <br />
              We use world-renowned skincare experts, makeup artists, leading
              dermatologists and nutrition gurus to help us create the ultimate
              in anti-ageing, exfoliation, the very best makeup textures, and
              super-efficacious ingredient-loaded formulas.
            </>
          ) : (
            "Comparison is based on a luxury product of similar quality, efficacy and size/weight:"
          )}
        </p>
        {!isPCV2 && <DupeTable productComparison={productComparison} />}
        <DrawerFooter onClose={onClose} variantData={variantData} />
      </div>
    </OverlayDrawer>
  )
}
